import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Palette, Save, RotateCcw } from 'lucide-react';
import { themeService } from '../services/themeService';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'sonner';

export const ThemeEditor = () => {
  const { theme, reloadTheme } = useTheme();
  const [colors, setColors] = useState({
    primary: '#0F7BC0',
    secondary: '#4CAF50',
    text_primary: '#1a1a1a',
    text_secondary: '#666666',
    text_on_primary: '#ffffff',
    background: '#F8FAFC',
    surface: '#FFFFFF',
    surface_secondary: '#7791A9',
    border: '#E2E8F0',
    success: '#4CAF50',
    warning: '#FF9800',
    error: '#EF4444',
    info: '#2196F3'
  });
  const [loading, setLoading] = useState(false);
  const [themes, setThemes] = useState([]);

  useEffect(() => {
    if (theme?.colors) {
      setColors(theme.colors);
    }
    loadThemes();
  }, [theme]);

  const loadThemes = async () => {
    try {
      const data = await themeService.getAllThemes();
      setThemes(data);
    } catch (error) {
      console.error('Error loading themes:', error);
    }
  };

  const handleColorChange = (key, value) => {
    setColors(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      if (theme?.theme_id) {
        await themeService.updateTheme(theme.theme_id, { colors });
      } else {
        const newTheme = await themeService.createTheme({
          name: 'Custom Theme',
          colors
        });
        await themeService.updateTheme(newTheme.theme_id, { is_active: true });
      }
      
      await reloadTheme();
      toast.success('Theme erfolgreich gespeichert und angewendet!');
    } catch (error) {
      toast.error('Fehler beim Speichern des Themes');
      console.error('Error saving theme:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setColors({
      primary: '#0F7BC0',
      secondary: '#4CAF50',
      text_primary: '#1a1a1a',
      text_secondary: '#666666',
      text_on_primary: '#ffffff',
      background: '#F8FAFC',
      surface: '#FFFFFF',
      surface_secondary: '#7791A9',
      border: '#E2E8F0',
      success: '#4CAF50',
      warning: '#FF9800',
      error: '#EF4444',
      info: '#2196F3'
    });
  };

  const colorFields = [
    { key: 'primary', label: 'Primärfarbe (Aktive Buttons)', description: 'Hauptfarbe für Buttons und Aktionen' },
    { key: 'secondary', label: 'Sekundärfarbe', description: 'Sekundäre Akzentfarbe' },
    { key: 'text_primary', label: 'Primärer Text', description: 'Haupt-Textfarbe (Schwarz)' },
    { key: 'text_secondary', label: 'Sekundärer Text', description: 'Sekundärer/Grauer Text' },
    { key: 'text_on_primary', label: 'Text auf Primärfarbe', description: 'Textfarbe auf aktiven Buttons (Weiss)' },
    { key: 'background', label: 'Hintergrund', description: 'Haupt-Hintergrundfarbe' },
    { key: 'surface', label: 'Oberfläche', description: 'Karten und Panel-Farbe' },
    { key: 'surface_secondary', label: 'Sekundäroberfläche', description: 'Sekundäre Oberfläche für Akzente' },
    { key: 'border', label: 'Rahmen', description: 'Rahmen- und Trennlinien-Farbe' },
  ];

  const statusColors = [
    { key: 'success', label: 'Erfolg', description: 'Erfolgs-Status' },
    { key: 'warning', label: 'Warnung', description: 'Warnungs-Status' },
    { key: 'error', label: 'Fehler', description: 'Fehler-Status' },
    { key: 'info', label: 'Information', description: 'Info-Status' },
  ];

  return (
    <div className="space-y-6" data-testid="theme-editor">
      <Card style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Palette className="w-5 h-5" style={{ color: 'var(--color-primary)' }} />
            <CardTitle style={{ color: 'var(--color-text-primary)' }}>Theme-Editor</CardTitle>
          </div>
          <CardDescription style={{ color: 'var(--color-text-secondary)' }}>
            Passen Sie die Farben Ihrer Portal-Oberfläche an
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>
                Hauptfarben
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                {colorFields.map(field => (
                  <div key={field.key} className="space-y-2">
                    <Label htmlFor={field.key} style={{ color: 'var(--color-text-primary)' }}>
                      {field.label}
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id={field.key}
                        type="color"
                        value={colors[field.key]}
                        onChange={(e) => handleColorChange(field.key, e.target.value)}
                        className="w-20 h-10 cursor-pointer"
                        data-testid={`color-input-${field.key}`}
                      />
                      <Input
                        type="text"
                        value={colors[field.key]}
                        onChange={(e) => handleColorChange(field.key, e.target.value)}
                        className="flex-1"
                        placeholder="#0F7BC0"
                        style={{ borderColor: 'var(--color-border)' }}
                      />
                    </div>
                    <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                      {field.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>
                Status-Farben
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                {statusColors.map(field => (
                  <div key={field.key} className="space-y-2">
                    <Label htmlFor={field.key} style={{ color: 'var(--color-text-primary)' }}>
                      {field.label}
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id={field.key}
                        type="color"
                        value={colors[field.key]}
                        onChange={(e) => handleColorChange(field.key, e.target.value)}
                        className="w-20 h-10 cursor-pointer"
                        data-testid={`color-input-${field.key}`}
                      />
                      <Input
                        type="text"
                        value={colors[field.key]}
                        onChange={(e) => handleColorChange(field.key, e.target.value)}
                        className="flex-1"
                        style={{ borderColor: 'var(--color-border)' }}
                      />
                    </div>
                    <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                      {field.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>
                Vorschau
              </h3>
              <div className="p-6 rounded-lg space-y-4" style={{ backgroundColor: colors.background }}>
                <Button 
                  style={{ 
                    backgroundColor: colors.primary, 
                    color: colors.text_on_primary 
                  }}
                  className="mr-2"
                >
                  Primärer Button
                </Button>
                <Button 
                  variant="outline"
                  style={{ 
                    borderColor: colors.primary,
                    color: colors.primary
                  }}
                >
                  Sekundärer Button
                </Button>
                <div className="p-4 rounded-lg mt-4" style={{ backgroundColor: colors.surface, borderColor: colors.border, borderWidth: '1px' }}>
                  <h4 className="font-semibold mb-2" style={{ color: colors.text_primary }}>
                    Beispiel-Karte
                  </h4>
                  <p style={{ color: colors.text_secondary }}>
                    Dies ist eine Vorschau, wie Ihre Farben im Portal aussehen werden.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
              <Button
                onClick={handleSave}
                disabled={loading}
                style={{ 
                  backgroundColor: 'var(--color-primary)', 
                  color: 'var(--color-text-on-primary)' 
                }}
                data-testid="save-theme-button"
              >
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Speichern...' : 'Theme speichern'}
              </Button>
              <Button
                onClick={handleReset}
                variant="outline"
                style={{ 
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-text-primary)'
                }}
                data-testid="reset-theme-button"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Zuruecksetzen
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
